import React from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { useTooltip } from '@weave/design-system';
import {
  ChecklistMetricCriteria,
  ChecklistMetricStatus,
  MultiLocationResult,
} from '../../handoff-checklist-v2.types';
import { ReactComponent as StatusWarningIcon } from 'images/status-warning.svg';
import { ReactComponent as StatusErrorIcon } from 'images/status-error.svg';

export interface StatusBadgeProps {
  status: ChecklistMetricStatus;
  isLoading?: boolean;
  locationResults?: MultiLocationResult[];
  allowExceptions?: boolean;
}

export interface MultiLocationStatusBadgeProps {
  locationResults: MultiLocationResult[];
  allowExceptions?: boolean;
  isLoading?: boolean;
  status?: ChecklistMetricStatus;
}

const isUnresolvedFailure = (criterion: ChecklistMetricCriteria) =>
  criterion.status !== ChecklistMetricStatus.PASSED &&
  criterion.status !== ChecklistMetricStatus.EXCEPTION_NOTED;

const isExceptionCapableFailure = (
  criterion: ChecklistMetricCriteria,
  allowExceptions: boolean
) =>
  isUnresolvedFailure(criterion) &&
  allowExceptions &&
  criterion.exceptionType !== undefined &&
  criterion.exceptionType !== 'none';

const isHardFailure = (criterion: ChecklistMetricCriteria, allowExceptions: boolean) =>
  isUnresolvedFailure(criterion) &&
  (!allowExceptions ||
    criterion.exceptionType === undefined ||
    criterion.exceptionType === 'none');

export const MultiLocationStatusBadge = ({
  locationResults,
  allowExceptions = false,
  isLoading,
  status,
}: MultiLocationStatusBadgeProps) => {
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ placement: 'top' });

  if (isLoading || !locationResults || locationResults.length === 0) {
    return null;
  }

  const nonPassedLocations = locationResults.filter((location) =>
    location.criterias.some(
      (criterion) => criterion.status !== ChecklistMetricStatus.PASSED
    )
  );

  const nonPassedCount = nonPassedLocations.length;

  if (nonPassedCount === 0) {
    return null;
  }

  // All exceptions have been noted — show the "Exception Noted" badge
  if (status === ChecklistMetricStatus.EXCEPTION_NOTED) {
    const locationText =
      nonPassedCount === 1
        ? 'Selected Location Exception Noted'
        : 'Selected Locations Exception Noted';

    return (
      <span css={styles.exceptionNotedBadge}>
        <span css={styles.warningBullet}>
          <StatusWarningIcon css={{ width: 16, height: 16 }} />
        </span>
        {`${nonPassedCount} ${locationText}`}
      </span>
    );
  }

  const hardFailedLocations = locationResults.filter((location) =>
    location.criterias.some((c) => isHardFailure(c, allowExceptions))
  );

  const exceptionRequiredLocations = locationResults.filter((location) =>
    location.criterias.some((c) => isExceptionCapableFailure(c, allowExceptions))
  );

  const hardCount = hardFailedLocations.length;
  const exceptionCount = exceptionRequiredLocations.length;

  if (hardCount === 0 && exceptionCount === 0) {
    return null;
  }

  const failedText =
    hardCount === 1 ? 'Selected Location Failed' : 'Selected Locations Failed';

  const exceptionText =
    exceptionCount === 1
      ? 'Selected Location Requires Exceptions'
      : 'Selected Locations Require Exceptions';

  return (
    <>
      {hardCount > 0 && (
        <span css={styles.blockedBadge}>
          <span css={styles.blockedBullet}>
            <StatusErrorIcon css={{ width: 16, height: 16 }} />
          </span>
          {`${hardCount} ${failedText}`}
        </span>
      )}
      {exceptionCount > 0 && (
        <span css={styles.warningBadge} {...triggerProps}>
          <span css={styles.warningBullet}>
            <StatusWarningIcon css={{ width: 16, height: 16 }} />
          </span>
          {`${exceptionCount} ${exceptionText}`}
          <Tooltip {...tooltipProps} css={styles.tooltip}>
            Add exceptions and resolve the required checks to continue
          </Tooltip>
        </span>
      )}
    </>
  );
};

export const StatusBadge = ({
  status,
  isLoading,
  locationResults,
  allowExceptions = false,
}: StatusBadgeProps) => {
  if (locationResults && locationResults.length > 0) {
    return (
      <MultiLocationStatusBadge
        locationResults={locationResults}
        allowExceptions={allowExceptions}
        isLoading={isLoading}
        status={status}
      />
    );
  }

  if (isLoading || status === ChecklistMetricStatus.PASSED) {
    return null;
  }

  if (status === ChecklistMetricStatus.BLOCKED) {
    return (
      <span css={styles.blockedBadge}>
        <span css={styles.blockedBullet}>
          <StatusErrorIcon css={{ width: 16, height: 16 }} />
        </span>
        Blocked
      </span>
    );
  }

  if (status === ChecklistMetricStatus.WARNING) {
    return (
      <span css={styles.warningBadge}>
        <span css={styles.warningBullet}>
          <StatusWarningIcon css={{ width: 16, height: 16 }} />
        </span>
        Warning
      </span>
    );
  }

  if (status === ChecklistMetricStatus.EXCEPTION_NOTED) {
    return (
      <span css={styles.exceptionNotedBadge}>
        <span css={styles.warningBullet}>
          <StatusWarningIcon css={{ width: 16, height: 16 }} />
        </span>
        Exception Noted
      </span>
    );
  }

  return null;
};

const styles = {
  blockedBadge: css`
    display: inline-flex;
    align-items: center;
    background-color: #fff1f0;
    border: 1px solid #ffccc7;
    border-radius: 4px;
    color: #cf1322;
    padding: ${theme.spacing(0.5, 1)};
    font-size: 12px;
    font-weight: 500;
  `,
  blockedBullet: css`
    margin-right: ${theme.spacing(0.5)};
    font-weight: bold;
    display: inline-flex;
    align-items: center;
  `,
  warningBadge: css`
    display: inline-flex;
    align-items: center;
    background-color: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 4px;
    color: #d48806;
    padding: ${theme.spacing(0.5, 1)};
    font-size: 12px;
    font-weight: 500;
  `,
  exceptionNotedBadge: css`
    display: inline-flex;
    align-items: center;
    background-color: #fcf4e4;
    border: 1px solid #eec085;
    border-radius: 4px;
    color: #8c5b1b;
    padding: ${theme.spacing(0.5, 1)};
    font-size: 12px;
    font-weight: 500;
  `,
  warningBullet: css`
    margin-right: ${theme.spacing(0.5)};
    font-weight: bold;
    display: inline-flex;
    align-items: center;
  `,
  tooltip: css`
    && {
      max-width: 280px;
    }
  `,
};
